import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LocationSelector } from '../../assets/icons/location-selector.svg';
import ModalBox from '../modal-box/ModalBox';
import { TooltipIcon } from '../tooltip/TooltipIcon';

import SelectLatLong from './components/SelectLatLong';

interface SelectLocationProperties {
  inputLabel?: string;
  latitude: string | number | null;
  longitude: string | number | null;
  onChangeValue: (latitude: number, longitude: number, address?: string, geoLocation?: any[]) => void;
  valueRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  readOnly?: boolean;
  tooltipText?: string;
}

/**
 * Location selector shared component
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.latitude - The latitude prop
 * @param {any} param0.longitude - The longitude prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.tooltipText - The tooltipText prop
 */
function SelectLocation({
  inputLabel,
  latitude,
  longitude,
  onChangeValue,
  valueRequired,
  error,
  errorMessage,
  readOnly,
  tooltipText,
}: SelectLocationProperties) {
  const { t } = useTranslation('sharedComp');
  const [locationPopup, setLocationPopup] = useState<'open' | 'close'>('close');
  /**
   * Function to handle lat & long change
   * @param {any} lat - The lat
   * @param {any} long - The long
   * @param {any} address - The address
   */
  const handleValueChange = (lat: number, long: number, address?: string, geoLocation?: any[]) => {
    onChangeValue(lat, long, address, geoLocation);
    setLocationPopup('close');
  };

  return (
    <div className="select-location-wrap">
      <ModalBox
        open={locationPopup === 'open'}
        title={t('selectLocationTitle')}
        content={
          <SelectLatLong
            latitude={latitude}
            longitude={longitude}
            handleLatLongChange={handleValueChange}
          />
        }
        closeModal={() => setLocationPopup('close')}
        header
        modalBoxAnimation="modalAnimateToLeft"
      />
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
          {tooltipText && <TooltipIcon text={tooltipText} />}
        </p>
      )}
      <div className="select-location-lat-long">
        <div className={`select-location-container ${error && 'error'}`}>
          <span className='icon' role="presentation" onClick={() => setLocationPopup('open')}>
            <LocationSelector />
          </span>{' '}
          <input
            type="number"
            placeholder={t('latitudePlaceholder')}
            value={latitude || ''}
            onChange={(event) => handleValueChange(Number(event.target.value), Number(longitude))}
            required={valueRequired}
            readOnly={readOnly}
          />
        </div>
        <div className={`select-location-container ${error && 'error'}`}>
          <input
            type="number"
            placeholder={t('longitudePlaceholder')}
            value={longitude || ''}
            onChange={(event) => handleValueChange(Number(latitude), Number(event.target.value))}
            required={valueRequired}
            readOnly={readOnly}
          />
        </div>
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
    </div>
  );
}

SelectLocation.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  // readOnly: false,
  error: false,
  errorMessage: '',
  readOnly: false,
  tooltipText: '',
};

export default SelectLocation;
